import { FormGroup, UntypedFormGroup, ValidatorFn } from '@angular/forms';
import { ThemePalette } from '@angular/material/core';
import { TextMaskConfig } from 'angular2-text-mask';
import { Observable, Subscription } from 'rxjs';
import { AutocompleteFilterComponent } from './components/autocomlete-filter/autocomplete-filter.component';
import { InputFilterComponent } from './components/input-filter/input-filter.component';
import { RadioFilterItem } from './components/radio-filter/radio-filter.models';

export interface TableFilterButton {
  text: string;
  callback?: () => void;
  testId: string;
  autofocus?: boolean;
  class?: string;
  color?: ThemePalette;
}

export enum TableFilterType {
  Radio = 'radio',
  Input = 'input',
  Date = 'date',
  DoubleDate = 'double-date',
  NumberInput = 'number-input',
  DoubleNumberInput = 'double-number-input',
  PhoneNumber = 'phone-number',
  Checkbox = 'checkbox',
  Autocomplete = 'autocomplete',
  SearchInput = 'search-input',
}

export interface TableHotFilter {
  type: TableFilterType;
  quickButtonText: string;
  overlayTitle: string;
  overlayButtonText: string;
  order: number;
}

export interface TableFilterChipData {
  content: (value: any) => string;
  filtersToCancel?: { controlName: string, initialValue: any }[];
  tooltipText?: (value: any) => string;
  tooltipDisabled?: (value: any) => boolean;
}

export interface TableFilter {
  title: string;
  type: TableFilterType;
  testId: string;
  placeholder?: string;
  initialValue: any;
  apiFieldName: string;
  controlName?: string;
  errors?: Record<string, string>;
  validators?: ValidatorFn[];
  hotFilter?: TableHotFilter;
  onlyHotFilter?: boolean;
  displayCondition?: (form: FormGroup) => boolean;
  tooltipCallback?: (formGroup: UntypedFormGroup) => string;
  chipData?: TableFilterChipData;
}

export interface InputTableFilter extends TableFilter {
  applyTrim?: boolean;
  mask?: string | null;
  maxLength?: number;
  valueChangeSubscription?: (context: InputFilterComponent) => Subscription;
  disableValidation?: boolean;
}

export interface AutocompleteTableFilter extends TableFilter {
  maxLength?: number;
  emptyListNote?: string;
  service: object;
  methodName: string;
  listOptionName: string;
  displayWithCallback: ((value: any) => string) | null;
  valueChangeSubscription: (context: AutocompleteFilterComponent) => Subscription;
  initOnFocus: boolean;
  filterQuery?: (formGroup: UntypedFormGroup) => Observable<string>;
  hideArrowIfListEmpty?: boolean;
}

export interface PhoneNumberTableFilter extends TableFilter {
  textMask?: TextMaskConfig;
}

export interface RadioTableFilter extends TableFilter {
  options?: RadioFilterItem[];
}

export interface DateTableFilter extends TableFilter {
  minDate?: Date;
  maxDate?: Date;
}

export interface NumberInputTableFilter extends TableFilter {
  minValue?: number;
  maxValue?: number;
  digitsAfterDot?: number;
}

export interface RangeTableFilter extends TableFilter {
  fromFilter: DateTableFilter | NumberInputTableFilter;
  toFilter: DateTableFilter | NumberInputTableFilter;
}

export interface DoubleDateTableFilter extends RangeTableFilter {
  fromFilter: DateTableFilter;
  toFilter: DateTableFilter;
  todayBtn?: boolean;
}

export interface DoubleNumberInputTableFilter extends RangeTableFilter {
  fromFilter: NumberInputTableFilter;
  toFilter: NumberInputTableFilter;
}

export interface TableFilterGroup {
  title: string;
  controlName: string;
  filters: TableFilter[];
  testId?: string;
  expandedByDefault?: boolean;
  icon?: {
    name: string,
    isCustom?: boolean
  };
  hasClearBtn?: boolean;
}

export interface TableFilterConfig {
  buttons: Record<string, TableFilterButton>;
  title: string;
  filterGroups: TableFilterGroup[];
  storageKey: string;
}

export interface TableChip {
  groupName: string;
  tableFilterConfig: TableFilter,
  value: any;
}
